import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Button,
  Icon,
  IconButton,
  Notice,
  Page,
  ProfilePicturePicker,
  RankedList,
  Select,
  useConfirm,
  useQueryParam,
} from "@jkast90/kast-common/ui";
import { errorText } from "@jkast90/kast-common/api-client";
import { deletePicture, listPictures, uploadPicture } from "@jkast90/kast-common/kast-dev-api";
import { count } from "@jkast90/kast-common/format";
import { cloudSaves } from "../emulator/cloudSaves";
import { admin, signedInAdmin } from "../library/admin";
import { ProfileToolbar, useAdmin } from "@common/account";
import { hasGroup, useSession, useSignedIn } from "@jkast90/kast-common/auth";
import { ADMIN_GROUPS, session, signIn } from "../auth/session";
import { useOnline } from "@jkast90/kast-common/offline";
import Cover from "../library/Cover";
import { ago, lastActive, playerName, type Picture, type UserSummary } from "../library/dashboard";
import { SYSTEM_LABEL, type System } from "../library/localLibrary";
import { formatPlayed } from "../library/playtime";
import { queryKeys, saveKeys, useDashboard } from "../library/queries";
import { saveMyProfile } from "../library/profile";
import { displayName, romBaseName } from "../emulator/saves";

// A player's profile: who they are, how much they've played and of what,
// and what they have saved in the cloud. Yours by default; anyone in the
// family from the selector, since the dashboard feed has everyone.
export default function ProfilePage() {
  const { user } = useSession();
  const isAuthenticated = useSignedIn();
  const online = useOnline();
  const userEmail = user?.email || null;
  // The name claim only: user.name falls back to the email, shown beside it.
  const userName = typeof user?.claims.name === "string" ? user.claims.name.trim() || null : null;
  const { sub: me } = signedInAdmin();
  // Deleting someone else's saves is an admin tool, offered only while admin tools are on.
  const isAdmin = useAdmin().unlocked;
  const [confirm, confirmDialog] = useConfirm();
  const [deleting, setDeleting] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [userParam, setUserParam] = useQueryParam("user", useSearchParams());
  const feed = useDashboard(isAuthenticated);

  // You first, then everyone else by name.
  const players = useMemo(() => {
    const all = [...(feed.data?.players ?? [])];
    all.sort((a, b) => (a.sub === me ? -1 : b.sub === me ? 1 : playerName(a).localeCompare(playerName(b))));
    if (me && !all.some((p) => p.sub === me)) {
      all.unshift({
        sub: me,
        email: userEmail,
        name: userName,
        lastSave: null,
        games: [],
        played: [],
        secondsPlayed: 0,
        lastPlayed: null,
        picture: null,
      });
    }
    return all;
  }, [feed.data, me, userEmail, userName]);
  const chosenSub = userParam ?? me;
  const player: UserSummary | null = players.find((p) => p.sub === chosenSub) ?? players[0] ?? null;
  const isMe = player?.sub === me;
  const choose = (sub: string) => setUserParam(sub === me ? null : sub);

  const played = [...(player?.played ?? [])].sort((a, b) => b.lastPlayed.localeCompare(a.lastPlayed));
  // The picture: the game they chose, else the one they've played most.
  const mostPlayed = [...(player?.played ?? [])].sort((a, b) => b.seconds - a.seconds)[0];
  const coverId = player?.picture?.kind === "cover" ? player.picture.gameId : null;
  const poolPicture = player?.picture?.kind === "pool" ? player.picture : null;
  const pictureGame = (coverId && player?.played.find((g) => g.game === coverId)) || mostPlayed || null;
  const [choosing, setChoosing] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // The family's pool of this account's pictures: asked for while the picker is open.
  const pool = useQuery({
    queryKey: queryKeys.pictures,
    queryFn: () => listPictures({ token: session.bearer() }),
    enabled: choosing && isMe,
  });
  // Removes a game's cloud saves (the in-game save and its slots): your own
  // through the saves API, anyone's as an admin. Copies on devices stay.
  const deleteCloudSaves = async (game: string, states: number, battery: boolean) => {
    if (!player) return;
    const what = [battery && "the in-game save", states > 0 && count(states, "slot")].filter(Boolean).join(" and ");
    const ok = await confirm({
      title: `Delete ${displayName(game)}'s cloud saves?`,
      message: `${what.charAt(0).toUpperCase()}${what.slice(1)} ${isMe ? "you" : name} saved in the cloud ${states + (battery ? 1 : 0) === 1 ? "is" : "are"} removed. Copies on devices stay, and go back up from there. This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    setDeleting(game);
    setSaveError(null);
    try {
      const names = (isMe ? await cloudSaves.list() : await admin.saves(player.sub))
        .map((s) => s.name)
        .filter((n) => romBaseName(n) === game);
      for (const file of names) await (isMe ? cloudSaves.remove(file) : admin.deleteSave(player.sub, file));
      await Promise.all(saveKeys(player.sub, isMe).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Could not delete the saves.");
    } finally {
      setDeleting(null);
    }
  };

  const choosePicture = async (picture: Picture | null) => {
    await saveMyProfile({ picture });
    await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    setChoosing(false);
  };
  const chooseCover = async (game: string | null) => {
    setSaving(game ?? "");
    try {
      await choosePicture(game ? { kind: "cover", gameId: game } : null);
    } catch {
      // The feed keeps the old picture; the dialog stays open to try again.
    } finally {
      setSaving(null);
    }
  };
  const total = player?.secondsPlayed ?? 0;
  const error = feed.error ? errorText(feed.error, "Could not load profiles.") : null;
  const name = player ? (isMe ? "You" : playerName(player)) : "";
  const who = isMe ? "you" : name;

  return (
    <Page className="about-main">
      <div className="library-head profile-head">
        <h1>Profile</h1>
        <span className="profile-head-actions">
          <ProfileToolbar />
          {isAuthenticated && players.length > 1 && (
            <Select
              variant="bare"
              size="sm"
              value={player?.sub ?? ""}
              options={players.map((p) => ({ value: p.sub, label: p.sub === me ? "You" : playerName(p) }))}
              onChange={choose}
              aria-label="Whose profile"
              className="dropdown profile-picker"
            />
          )}
        </span>
      </div>
      {error && (
        <Notice tone="error" style={{ marginBottom: "1rem" }}>
          {error}
        </Notice>
      )}
      {confirmDialog}
      {choosing && player && (
        <ProfilePicturePicker
          open
          onClose={() => setChoosing(false)}
          name={playerName(player)}
          title="Your picture"
          pictures={pool.data?.map((p) => ({ id: p.id, url: p.url, createdAt: p.created_at, app: p.app }))}
          error={pool.error ? errorText(pool.error, "Could not load your pictures.") : null}
          current={poolPicture?.id ?? null}
          onSelect={(picked) =>
            // The picker doesn't catch a failed save; the feed keeps the old picture and the dialog stays open.
            choosePicture(picked ? { kind: "pool", id: picked.id, url: picked.url } : null).catch(() => undefined)
          }
          onUpload={async (file) => {
            const entry = await uploadPicture(file, { token: session.bearer(), app: "pocket-play" });
            await queryClient.invalidateQueries({ queryKey: queryKeys.pictures });
            return { id: entry.id, url: entry.url, app: entry.app };
          }}
          onDelete={async (picked) => {
            await deletePicture(picked.id, { token: session.bearer() });
            await queryClient.invalidateQueries({ queryKey: queryKeys.pictures });
          }}
          extraTabs={[
            {
              id: "covers",
              label: "Game covers",
              content:
                player.played.length === 0 ? (
                  <p className="kui-muted kui-small">Play a game while signed in and its cover can be your picture.</p>
                ) : (
                  <>
                    <p className="kui-muted kui-small">
                      The cover of one of your games. Without a choice it's the one you've played most.
                    </p>
                    <ul className="kui-list-reset picture-grid">
                      {[...player.played]
                        .sort((a, b) => b.seconds - a.seconds)
                        .map((game) => (
                          <li key={game.game}>
                            <button
                              type="button"
                              className={`picture-option${coverId === game.game ? " selected" : ""}`}
                              disabled={saving !== null}
                              onClick={() => chooseCover(game.game)}
                              aria-label={displayName(game.name)}
                              data-tip={displayName(game.name)}
                            >
                              {saving === game.game ? (
                                <span className="spinner" aria-hidden="true" />
                              ) : (
                                <Cover name={game.name} className="picture-option-cover" />
                              )}
                            </button>
                          </li>
                        ))}
                    </ul>
                    {coverId && (
                      <Button disabled={saving !== null} onClick={() => chooseCover(null)}>
                        Back to most played
                      </Button>
                    )}
                  </>
                ),
            },
          ]}
        />
      )}

      {!isAuthenticated ? (
        <section className="panel">
          <h2>Account</h2>
          <p>Sign in to see your play time, your games and your cloud saves, and everyone else's.</p>
          <Button variant="primary" disabled={!online} onClick={signIn}>
            {online ? "Sign in" : "Signing in needs a connection"}
          </Button>
        </section>
      ) : !player ? (
        <p className="kui-muted">{feed.isLoading ? "Loading…" : "Nobody here yet."}</p>
      ) : (
        <>
          <section className="panel">
            <h2>Account</h2>
            <div className="profile-account">
              <button
                type="button"
                className={`profile-picture${isMe ? " mine" : ""}`}
                disabled={!isMe}
                aria-label={isMe ? "Choose a picture" : undefined}
                data-tip={
                  isMe ? "Choose a picture" : !poolPicture && pictureGame ? displayName(pictureGame.name) : undefined
                }
                onClick={() => setChoosing(true)}
              >
                {poolPicture ? (
                  <Avatar name={playerName(player)} src={poolPicture.url} size="lg" />
                ) : pictureGame ? (
                  <Cover name={pictureGame.name} className="profile-picture-cover" />
                ) : (
                  <Icon name="videogame_asset" size={20} className="icon" />
                )}
                {isMe && (
                  <span className="profile-picture-edit">
                    <Icon name="edit" size={20} className="icon" />
                  </span>
                )}
              </button>
              <div>
                <strong>
                  {(isMe ? (userName ?? player.name) : player.name) ??
                    (isMe ? userEmail : (player.email ?? playerName(player)))}
                </strong>
                <span className="kui-muted kui-small">
                  {(isMe ? (userName ?? player.name) : player.name) && (isMe ? userEmail : player.email)
                    ? `${isMe ? userEmail : player.email} · `
                    : ""}
                  kast-dev account{isMe && hasGroup(user, ADMIN_GROUPS) ? " · CloudBoy admin" : ""}
                </span>
                <span className="kui-muted kui-small">
                  {[
                    player.lastPlayed && `last played ${ago(player.lastPlayed)}`,
                    player.lastSave && `last saved ${ago(player.lastSave)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "no activity yet"}
                </span>
              </div>
              <div className="library-stats profile-stats">
                <div>
                  <span className="kui-muted kui-small">Played</span>
                  <strong>{total ? formatPlayed(total) : "None yet"}</strong>
                </div>
                <div>
                  <span className="kui-muted kui-small">Games</span>
                  <strong>{played.length}</strong>
                </div>
                <div>
                  <span className="kui-muted kui-small">Last active</span>
                  <strong>{lastActive(player) ? ago(lastActive(player)!) : "Never"}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="panel">
            <h2>Games</h2>
            {played.length === 0 ? (
              <p className="kui-muted kui-small">
                {isMe ? "Time shows here once you play while signed in." : `${name} hasn't played while signed in yet.`}
              </p>
            ) : (
              <RankedList
                aria-label="Games played"
                items={played.map((game) => ({
                  key: game.game,
                  leading: <Cover name={game.name} className="rank-cover" />,
                  title: (
                    <Link to={`/library?${new URLSearchParams({ game: romBaseName(game.name) })}`}>
                      {displayName(game.name)}
                    </Link>
                  ),
                  meta: SYSTEM_LABEL[game.system as System] ?? game.system,
                  value: (
                    <span className="profile-game-time">
                      <strong>{formatPlayed(game.seconds)}</strong>
                      <span className="kui-muted kui-small">played {ago(game.lastPlayed)}</span>
                    </span>
                  ),
                }))}
              />
            )}
          </section>

          <section className="panel">
            <h2>Cloud saves</h2>
            {player.games.length === 0 ? (
              <p className="kui-muted kui-small">
                {isMe
                  ? "No saves in the cloud yet. They go up as you play while signed in."
                  : `No cloud saves from ${who} yet.`}
              </p>
            ) : (
              <>
                <p className="kui-muted kui-small">
                  {count(player.games.length, "game")}
                  {player.lastSave && ` · last saved ${ago(player.lastSave)}`}
                </p>
                {saveError && (
                  <Notice tone="error" style={{ marginBottom: "1rem" }}>
                    {saveError}
                  </Notice>
                )}
                <ul className="kui-list-reset profile-saves">
                  {player.games.map((game) => (
                    <li key={game.game}>
                      <Link to={`/library?${new URLSearchParams({ game: game.game })}`} className="profile-save-game">
                        <Cover name={game.game} className="rank-cover" />
                        {displayName(game.game)}
                      </Link>
                      <span className="profile-save-meta">
                        <span className="kui-muted kui-small">
                          {[game.hasBatterySave && "in-game save", game.states > 0 && count(game.states, "slot")]
                            .filter(Boolean)
                            .join(" · ")}{" "}
                          · {ago(game.lastSave)}
                        </span>
                        {(isMe || isAdmin) && (
                          <IconButton
                            label="Delete these cloud saves"
                            className="btn icon-btn danger"
                            disabled={deleting !== null}
                            onClick={() => deleteCloudSaves(game.game, game.states, game.hasBatterySave)}
                          >
                            <Icon name="delete" size={20} className="icon" />
                          </IconButton>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </>
      )}
    </Page>
  );
}
